import { CalendarDays, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { DayPicker, type DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';

import type { AnalyticsRange } from '../lib/api';
import { daySpan, formatDateRange, localIsoDate } from './format';

export const MAX_RANGE_DAYS = 366;
const PRESETS = [7, 30, 90] as const;

function toDate(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/** The "Period" control: 7D/30D/90D shortcuts followed by a two-month range calendar. */
export function PeriodPicker(props: {
  start: string | null;
  end: string | null;
  range: AnalyticsRange;
  disabled?: boolean;
  onChange: (range: AnalyticsRange) => void;
}) {
  const { start, end, range, disabled, onChange } = props;
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const today = toDate(localIsoDate());

  useEffect(() => {
    if (!open) return;
    const closeOutside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', closeOutside);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('pointerdown', closeOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  function toggle() {
    if (!open) setDraft(start && end ? { from: toDate(start), to: toDate(end) } : undefined);
    setOpen((value) => !value);
  }

  function choose(next: AnalyticsRange) {
    onChange(next);
    if (open) {
      setOpen(false);
      trigger.current?.focus();
    }
  }

  function pickDay(day: Date) {
    setDraft((current) => {
      if (!current?.from || current.to) return { from: day, to: undefined };
      return day < current.from ? { from: day, to: current.from } : { from: current.from, to: day };
    });
  }

  const draftStart = draft?.from ? localIsoDate(draft.from) : null;
  const draftEnd = draft?.to ? localIsoDate(draft.to) : draftStart;
  const tooLong = !!draftStart && !!draftEnd && daySpan(draftStart, draftEnd) > MAX_RANGE_DAYS;
  const label = start && end ? formatDateRange(start, end) : 'Select dates';
  const rightMonth = draft?.to ?? draft?.from ?? (end ? toDate(end) : today);
  const defaultMonth = new Date(rightMonth.getFullYear(), rightMonth.getMonth() - 1, 1);

  return (
    <div className="period-picker" ref={root}>
      <span className="period-label" id="period-label">Period</span>
      <div className="segmented" role="group" aria-labelledby="period-label">
        {PRESETS.map((days) => {
          const active = 'days' in range && range.days === days;
          return (
            <button key={days} type="button" className={active ? 'segmented-active' : ''} aria-pressed={active}
              aria-label={`Last ${days} days`} disabled={disabled} onClick={() => choose({ days })}>
              {days}D
            </button>
          );
        })}
      </div>
      <button
        ref={trigger}
        type="button"
        className="period-trigger"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-labelledby="period-label period-value"
        disabled={disabled}
        onClick={toggle}
      >
        <CalendarDays size={17} aria-hidden="true" />
        <span id="period-value">{label}</span>
        <ChevronDown size={16} aria-hidden="true" />
      </button>

      {open ? (
        <div className="period-popover" role="dialog" aria-label="Choose a period">
          <div className="period-calendar">
            <DayPicker
              mode="range"
              numberOfMonths={2}
              selected={draft}
              onSelect={(_selected, day) => pickDay(day)}
              defaultMonth={defaultMonth}
              endMonth={today}
              disabled={{ after: today }}
              showOutsideDays={false}
            />
            <div className="period-footer">
              <span className={tooLong ? 'period-hint period-hint-error' : 'period-hint'}>
                {tooLong
                  ? `Choose ${MAX_RANGE_DAYS} days or fewer.`
                  : draftStart && draftEnd
                    ? `${formatDateRange(draftStart, draftEnd)} · ${daySpan(draftStart, draftEnd)} day${daySpan(draftStart, draftEnd) === 1 ? '' : 's'}`
                    : 'Pick a start date, then an end date.'}
              </span>
              <button type="button" onClick={() => setOpen(false)}>Cancel</button>
              <button type="button" className="period-apply" disabled={!draftStart || !draftEnd || tooLong}
                onClick={() => draftStart && draftEnd && choose({ start: draftStart, end: draftEnd })}>
                Apply
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
